/**
 * Expands each layout item up to maxW/maxH when there is free space
 * to the right or below without colliding with other items.
 * Used after drag/resize to auto-expand widgets when space is available.
 *
 * @param {Array<{i: string, x: number, y: number, w: number, h: number, minW?, maxW?, minH?, maxH?}>} layout
 * @param {number} cols - Number of grid columns
 * @returns {Array} Layout with expanded items (new array, does not mutate original)
 */
export const expandWidgetsToAvailableSpace = (layout, cols) => {
  if (!layout?.length) return layout;

  const result = layout.map((item) => ({ ...item }));

  const overlaps = (rect, excludeId) => {
    const [rx, ry, rw, rh] = rect;
    for (const other of result) {
      if (other.i === excludeId) continue;
      const ox2 = other.x + other.w;
      const oy2 = other.y + other.h;
      const rx2 = rx + rw;
      const ry2 = ry + rh;
      if (other.x < rx2 && rx < ox2 && other.y < ry2 && ry < oy2) {
        return true;
      }
    }
    return false;
  };

  const sorted = [...result].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });

  for (const item of sorted) {
    const maxW = item.maxW ?? Infinity;
    const maxH = item.maxH ?? Infinity;

    let newW = item.w;
    let newH = item.h;

    // Expand width: space to the right
    const maxExpandRight = Math.min(maxW - item.w, cols - (item.x + item.w));
    if (maxExpandRight > 0) {
      for (let dw = maxExpandRight; dw >= 1; dw--) {
        const rect = [item.x + item.w, item.y, dw, item.h];
        if (!overlaps(rect, item.i)) {
          newW = item.w + dw;
          break;
        }
      }
    }

    item.w = newW;

    // Expand height: space below (uses already expanded width)
    const maxExpandDown = maxH - item.h;
    if (maxExpandDown > 0) {
      for (let dh = maxExpandDown; dh >= 1; dh--) {
        const rect = [item.x, item.y + item.h, item.w, dh];
        if (!overlaps(rect, item.i)) {
          newH = item.h + dh;
          break;
        }
      }
    }

    item.h = newH;
  }

  return result;
};
